/**
 * Parent Dashboard — Redesigned
 * Rich insights-first layout: learning heatmap, subject mastery, achievements,
 * "wow moment", actionable recommendations, and contextual upgrade prompts.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Clock, MessageSquare, AlertTriangle,
  Plus, Settings, Eye, Shield,
  BarChart2, RefreshCw, Play, Crown, Zap,
  Share2, Check, Copy, BookOpen, Flame,
  TrendingUp, Trophy, Star, Lightbulb,
  ChevronRight, Target, Brain,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useChildStore, useAuthStore } from '../hooks';
import { analyticsService, getErrorMessage, chatService, type ProgressSummary } from '../services';
import { getSubscriptionStatus, createCheckout } from '../services/subscriptionService';
import { apiClient } from '../services/api';
import { getParentInsights } from '../services/insightsService';
import type { SubscriptionPlan, ParentInsights } from '@kids-lms/types';
import { GRADE_LEVEL_NAMES, SUBJECT_ICONS, SUBJECT_DISPLAY_NAMES, MASTERY_DISPLAY_NAMES } from '@kids-lms/constants';
import type { Subject } from '@kids-lms/types';

interface ChildStats {
  todayMinutes: number;
  totalQuestions: number;
  flaggedCount: number;
}


function WeeklyHeatmap({ activity }: { activity: ParentInsights['weeklyActivity'] }) {
  const max = Math.max(...activity.map((d) => d.sessions), 1);
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 mb-2">This Week's Activity</p>
      <div className="flex gap-2 items-end">
        {activity.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full relative" style={{ height: 40 }}>
              <motion.div
                className={`absolute bottom-0 w-full rounded-t-sm ${d.active ? 'bg-gradient-to-t from-kid-violet to-kid-ocean' : 'bg-gray-100'}`}
                initial={{ height: 0 }}
                animate={{ height: d.active ? Math.max(8, (d.sessions / max) * 40) : 6 }}
                transition={{ delay: i * 0.07, duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            <span className="text-[9px] text-gray-400 font-kid">{d.day}</span>
            {d.sessions > 0 && (
              <span className="text-[9px] text-kid-violet font-bold">{d.sessions}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, bg, iconColor, trend }: {
  icon: React.ElementType; label: string; value: string | number;
  bg: string; iconColor: string; trend?: string;
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-kid-surface rounded-kid shadow-kid border-2 border-kid-border p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 ${bg} rounded-kid-sm flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-kid-xs text-kid-text-soft font-kid">{label}</p>
          <p className="text-xl font-display text-kid-text">{value}</p>
          {trend && <p className="text-[10px] text-green-500 font-kid">{trend}</p>}
        </div>
      </div>
    </motion.div>
  );
}

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { children, selectedChild, isLoading, error, fetchChildren, setSelectedChild } = useChildStore();

  const [childStats, setChildStats] = useState<Record<string, ChildStats>>({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [subscription, setSubscription] = useState<{ plan: SubscriptionPlan; status: string } | null>(null);
  const [upgradingPlan, setUpgradingPlan] = useState<SubscriptionPlan | null>(null);
  const [copied, setCopied] = useState(false);
  const [referralStats, setReferralStats] = useState<{ referralCode: string | null; totalReferred: number; totalConverted: number } | null>(null);
  const [progressSummary, setProgressSummary] = useState<ProgressSummary | null>(null);
  const [insights, setInsights] = useState<ParentInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const masteryColors: Record<string, string> = {
    beginner: 'bg-kid-dandelion',
    developing: 'bg-kid-ocean',
    proficient: 'bg-kid-meadow',
    advanced: 'bg-kid-violet',
  };

  useEffect(() => {
    fetchChildren();
    getSubscriptionStatus().then((data) => setSubscription({ plan: data.plan, status: data.status })).catch(() => {});
    apiClient.get('/auth/referral-stats').then((res) => { if (res.data.success) setReferralStats(res.data.data); }).catch(() => {});
  }, [fetchChildren]);

  useEffect(() => {
    const fetchStats = async () => {
      if (children.length === 0) return;
      setLoadingStats(true);
      const stats: Record<string, ChildStats> = {};
      for (const child of children) {
        try {
          const summary = await analyticsService.getChildSummary(child.id);
          stats[child.id] = {
            todayMinutes: Math.round(summary.conversations.avgSessionMinutes * summary.conversations.total / 30) || 0,
            totalQuestions: summary.conversations.totalMessages || 0,
            flaggedCount: summary.flags.total || 0,
          };
        } catch {
          stats[child.id] = { todayMinutes: 0, totalQuestions: 0, flaggedCount: 0 };
        }
      }
      setChildStats(stats);
      setLoadingStats(false);
    };
    fetchStats();
  }, [children]);

  useEffect(() => {
    if (!selectedChild) return;
    chatService.getProgressSummary(selectedChild.id).then(setProgressSummary).catch(() => setProgressSummary(null));
    setLoadingInsights(true);
    getParentInsights(selectedChild.id)
      .then(setInsights)
      .catch(() => setInsights(null))
      .finally(() => setLoadingInsights(false));
  }, [selectedChild]);

  const handleUpgrade = async (plan: SubscriptionPlan) => {
    try {
      setUpgradingPlan(plan);
      const { checkoutUrl } = await createCheckout(plan, 'monthly');
      window.location.href = checkoutUrl;
    } catch {
      setUpgradingPlan(null);
    }
  };

  const shareUrl = `${window.location.origin}/?ref=${referralStats?.referralCode || user?.id?.slice(0, 8) || 'mindora'}`;
  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const totalStats = {
    children: children.length,
    todayMinutes: Object.values(childStats).reduce((sum, s) => sum + s.todayMinutes, 0),
    totalQuestions: Object.values(childStats).reduce((sum, s) => sum + s.totalQuestions, 0),
    flaggedCount: Object.values(childStats).reduce((sum, s) => sum + s.flaggedCount, 0),
  };

  if (isLoading && children.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 mx-auto text-primary-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center p-8 max-w-md">
          <AlertTriangle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{getErrorMessage(error)}</p>
          <button onClick={() => fetchChildren()} className="btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

      {/* ── Hero Banner ── */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-kid overflow-hidden shadow-kid-lg mb-6">
        <img src="/parent_dashboard_bg.png" alt="Mindora Parent Dashboard" className="w-full h-40 sm:h-52 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-kid-violet/70 via-kid-ocean/40 to-transparent flex items-end">
          <div className="p-5 sm:p-8 text-white">
            <h1 className="text-2xl sm:text-3xl font-display mb-1 drop-shadow-lg">
              {selectedChild ? `${selectedChild.name}'s Learning Hub` : `Welcome back, ${user?.email?.split('@')[0] || 'Parent'}!`}
            </h1>
            <p className="text-white/90 text-sm sm:text-base max-w-md drop-shadow font-kid">
              {insights?.recommendation || "Here's how your children are learning"}
            </p>
          </div>
        </div>
        <div className="absolute top-4 right-4">
          <Link to="/parent/settings" className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm text-kid-text rounded-kid-sm shadow-kid hover:bg-white transition-colors font-kid font-semibold text-sm">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </Link>
        </div>
      </motion.div>

      {/* ── Top Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Users} label="Children" value={totalStats.children} bg="bg-kid-ocean/10" iconColor="text-kid-ocean" />
        <StatCard icon={Clock} label="Today's Time" value={loadingStats ? '...' : `${totalStats.todayMinutes} min`} bg="bg-kid-meadow/10" iconColor="text-kid-meadow" />
        <StatCard icon={MessageSquare} label="Questions" value={loadingStats ? '...' : totalStats.totalQuestions} bg="bg-kid-violet/10" iconColor="text-kid-violet" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/parent/flagged" className={`block bg-kid-surface rounded-kid shadow-kid border-2 p-4 hover:shadow-kid-lg transition-shadow ${totalStats.flaggedCount > 0 ? 'border-red-200' : 'border-kid-border'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-kid-sm ${totalStats.flaggedCount > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${totalStats.flaggedCount > 0 ? 'text-red-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className="text-kid-xs text-kid-text-soft font-kid">Flagged</p>
                <p className="text-xl font-display text-kid-text">{loadingStats ? '...' : totalStats.flaggedCount}</p>
              </div>
            </div>
          </Link>
        </motion.div>
      </div>

      {/* ── Children Cards ── */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Your Children</h2>
        <Link to="/parent/add-child" className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
          <Plus className="w-4 h-4" /> Add Child
        </Link>
      </div>

      {children.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 text-center py-12 mb-8">
          <Users className="w-14 h-14 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">No Children Added Yet</h3>
          <p className="text-gray-500 mb-4 text-sm">Add your first child to start their learning journey.</p>
          <Link to="/parent/add-child" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600">
            <Plus className="w-4 h-4" /> Add Your First Child
          </Link>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {children.map((child, index) => {
            const stats = childStats[child.id] || { todayMinutes: 0, totalQuestions: 0, flaggedCount: 0 };
            const isSelected = selectedChild?.id === child.id;
            const pct = Math.min((stats.todayMinutes / (child.dailyTimeLimit || 60)) * 100, 100);
            return (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 p-4 cursor-pointer hover:shadow-md transition-all ${isSelected ? 'border-primary-500 ring-1 ring-primary-200' : 'border-gray-100 dark:border-gray-700'}`}
                onClick={() => setSelectedChild(child)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">{child.name[0].toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-800 dark:text-white text-sm truncate">{child.name}</h3>
                    <p className="text-xs text-gray-400">Age {child.age} · {GRADE_LEVEL_NAMES[child.gradeLevel] || `Grade ${child.gradeLevel}`}</p>
                  </div>
                  {isSelected && <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></div>}
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Daily Goal</span><span>{stats.todayMinutes}/{child.dailyTimeLimit || 60} min</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg py-1.5"><p className="text-sm font-bold text-gray-700 dark:text-gray-200">{stats.todayMinutes}m</p><p className="text-[9px] text-gray-400">Time</p></div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg py-1.5"><p className="text-sm font-bold text-gray-700 dark:text-gray-200">{stats.totalQuestions}</p><p className="text-[9px] text-gray-400">Questions</p></div>
                  <div className={`rounded-lg py-1.5 ${stats.flaggedCount > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}><p className={`text-sm font-bold ${stats.flaggedCount > 0 ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'}`}>{stats.flaggedCount}</p><p className="text-[9px] text-gray-400">Flagged</p></div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={(e) => { e.stopPropagation(); setSelectedChild(child); navigate('/learn'); }} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600">
                    <Play className="w-3 h-3" /> Learn
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/parent/child/${child.id}/conversations`); }} className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
                    <Eye className="w-3 h-3" /> Chats
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/parent/child/${child.id}/settings`); }} className="flex items-center justify-center p-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
                    <Settings className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ══ Selected Child Deep Dive ══ */}
      {selectedChild && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

          {/* ── Left: Insights + Charts (2 cols) ── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Quick Navigation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Play, color: 'text-primary-500', label: 'Start Learning', onClick: () => { setSelectedChild(selectedChild); navigate('/learn'); } },
                { icon: Eye, color: 'text-blue-500', label: 'Conversations', onClick: () => navigate(`/parent/child/${selectedChild.id}/conversations`) },
                { icon: BarChart2, color: 'text-green-500', label: 'Full Progress', onClick: () => navigate(`/parent/child/${selectedChild.id}/progress`) },
                { icon: Shield, color: 'text-amber-500', label: 'Controls', onClick: () => navigate(`/parent/child/${selectedChild.id}/settings`) },
              ].map((item) => (
                <button key={item.label} onClick={item.onClick} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-3 hover:shadow-md transition-shadow">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{item.label}</span>
                </button>
              ))}
            </div>

            {/* ── Insights Highlight Cards ── */}
            {loadingInsights ? (
              <div className="flex gap-3">
                {[1, 2, 3].map((i) => <div key={i} className="flex-1 h-20 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            ) : insights ? (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-kid-violet/10 to-kid-ocean/5 rounded-xl border border-kid-violet/20 p-3 text-center">
                  <Flame className="w-5 h-5 text-kid-tangerine mx-auto mb-1" />
                  <p className="text-xl font-display text-kid-text">{insights.weekSummary.currentStreak}</p>
                  <p className="text-[10px] text-gray-500 font-kid">Day Streak</p>
                </div>
                <div className="bg-gradient-to-br from-kid-meadow/10 to-kid-ocean/5 rounded-xl border border-kid-meadow/20 p-3 text-center">
                  <Clock className="w-5 h-5 text-kid-meadow mx-auto mb-1" />
                  <p className="text-xl font-display text-kid-text">{insights.weekSummary.totalMinutes}m</p>
                  <p className="text-[10px] text-gray-500 font-kid">This Week</p>
                </div>
                <div className="bg-gradient-to-br from-kid-dandelion/10 to-kid-tangerine/5 rounded-xl border border-kid-dandelion/20 p-3 text-center">
                  <Zap className="w-5 h-5 text-kid-dandelion mx-auto mb-1" />
                  <p className="text-xl font-display text-kid-text">Lv.{insights.xpStatus.level}</p>
                  <p className="text-[10px] text-gray-500 font-kid">{insights.xpStatus.levelTitle}</p>
                </div>
              </div>
            ) : null}

            {/* ── Weekly Heatmap + Subject Mastery ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Heatmap */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                <h3 className="text-sm font-bold text-gray-700 dark:text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" /> This Week's Activity
                </h3>
                {insights ? (
                  <>
                    <WeeklyHeatmap activity={insights.weeklyActivity} />
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-blue-600">{insights.weekSummary.totalSessions}</p>
                        <p className="text-[10px] text-gray-500">Chats</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-green-600">{insights.weekSummary.totalMessages}</p>
                        <p className="text-[10px] text-gray-500">Questions</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-purple-600">{insights.weekSummary.daysActive}</p>
                        <p className="text-[10px] text-gray-500">Active Days</p>
                      </div>
                    </div>
                    {insights.bestDay && (
                      <p className="text-[10px] text-gray-400 mt-3 font-kid text-center">
                        🏆 Most active on <strong>{insights.bestDay}s</strong>
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-center text-gray-400 text-sm py-8">No data yet this week</p>
                )}
              </div>

              {/* Subject Mastery */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                <h3 className="text-sm font-bold text-gray-700 dark:text-white mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-green-500" /> Subject Mastery
                </h3>
                {progressSummary && progressSummary.subjectProgress.length > 0 ? (
                  <div className="space-y-3">
                    {progressSummary.subjectProgress.map((sp) => (
                      <div key={sp.subject}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">{SUBJECT_ICONS[sp.subject as Subject] || '📚'}</span>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{SUBJECT_DISPLAY_NAMES[sp.subject as Subject] || sp.subject}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {sp.avgScore !== null && <span className="text-[10px] font-bold text-gray-500">{sp.avgScore}%</span>}
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full text-white ${masteryColors[sp.highestMastery] || 'bg-gray-400'}`}>
                              {MASTERY_DISPLAY_NAMES[sp.highestMastery as keyof typeof MASTERY_DISPLAY_NAMES] || sp.highestMastery}
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full ${masteryColors[sp.highestMastery] || 'bg-gray-400'}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${sp.avgScore || 20}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <span className="text-3xl block mb-2">📚</span>
                    <p className="text-gray-400 text-sm">No lessons completed yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Wow Moment + Recommendation ── */}
            {insights && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Highlight question */}
                {insights.highlightQuestion && (
                  <div className="bg-gradient-to-br from-kid-sky/10 to-kid-ocean/5 rounded-xl border border-kid-sky/30 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-kid-ocean" />
                      <p className="text-xs font-bold text-gray-600">Wow Moment This Week</p>
                    </div>
                    <p className="text-sm text-gray-700 italic font-kid">"{insights.highlightQuestion}"</p>
                    <p className="text-[10px] text-gray-400 mt-2 font-kid">— {selectedChild.name} asked this!</p>
                  </div>
                )}

                {/* Top / weak subject */}
                <div className="bg-gradient-to-br from-kid-meadow/10 to-kid-ocean/5 rounded-xl border border-kid-meadow/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-kid-meadow" />
                    <p className="text-xs font-bold text-gray-600">Learning Snapshot</p>
                  </div>
                  {insights.topSubject && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{SUBJECT_ICONS[insights.topSubject as Subject] || '📚'}</span>
                      <div>
                        <p className="text-xs text-gray-500 font-kid">Strongest</p>
                        <p className="text-sm font-bold text-gray-700">{SUBJECT_DISPLAY_NAMES[insights.topSubject as Subject] || insights.topSubject}</p>
                      </div>
                    </div>
                  )}
                  {insights.weakSubject && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💪</span>
                      <div>
                        <p className="text-xs text-gray-500 font-kid">Needs Practice</p>
                        <p className="text-sm font-bold text-gray-700">{SUBJECT_DISPLAY_NAMES[insights.weakSubject as Subject] || insights.weakSubject}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Recent Achievements ── */}
            {insights && insights.recentAchievements.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                <h3 className="text-sm font-bold text-gray-700 dark:text-white mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-kid-dandelion" /> Recent Achievements
                </h3>
                <div className="flex gap-3">
                  {insights.recentAchievements.map((a) => (
                    <div key={a.key} className="flex items-center gap-3 flex-1 bg-kid-dandelion/5 border border-kid-dandelion/20 rounded-lg p-3">
                      <span className="text-2xl">{a.emoji}</span>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-700 truncate">{a.title}</p>
                        <p className="text-[10px] text-gray-500 font-kid truncate">{a.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Actionable Recommendation ── */}
            {insights?.recommendation && (
              <div className="bg-gradient-to-r from-kid-violet/10 to-kid-ocean/5 border border-kid-violet/20 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-kid-violet/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-4 h-4 text-kid-violet" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-0.5">Mindora's Recommendation</p>
                  <p className="text-sm text-gray-700 font-kid">{insights.recommendation}</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar (1 col) ── */}
          <div className="space-y-4">

            {/* Upgrade CTA */}
            {subscription && subscription.plan === 'free' && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border border-purple-200 dark:border-purple-700 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-purple-600" />
                  <h3 className="font-bold text-gray-800 dark:text-white text-sm">Unlock More Learning</h3>
                </div>
                <ul className="space-y-1 mb-3">
                  {['50+ messages/day', 'Weekly email reports', 'Leaderboard & XP', 'Achievement badges'].map((f) => (
                    <li key={f} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Star className="w-3 h-3 text-yellow-400 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUpgrade('starter')}
                  disabled={upgradingPlan === 'starter'}
                  className="w-full py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Zap className="w-4 h-4" /> Upgrade — $9.99/mo
                </button>
                <Link to="/parent/settings" className="block mt-2 text-center text-[10px] text-purple-500 hover:underline">View all plans →</Link>
              </div>
            )}

            {/* Active subscription status */}
            {subscription && subscription.plan !== 'free' && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4 text-green-600" />
                  <p className="text-xs font-bold text-gray-600">Active Plan</p>
                </div>
                <p className="text-lg font-display text-green-700 capitalize">{subscription.plan}</p>
                <Link to="/parent/settings" className="text-[10px] text-green-600 hover:underline flex items-center gap-1 mt-1">Manage subscription <ChevronRight className="w-3 h-3" /></Link>
              </div>
            )}

            {/* Referral */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-gray-800 dark:text-white text-sm">Share Mindora</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">Invite friends — get a free month when they subscribe!</p>
              {referralStats && referralStats.totalReferred > 0 && (
                <div className="flex gap-3 mb-2 text-xs">
                  <span className="text-gray-500"><strong className="text-gray-700">{referralStats.totalReferred}</strong> invited</span>
                  <span className="text-gray-500"><strong className="text-green-600">{referralStats.totalConverted}</strong> subscribed</span>
                </div>
              )}
              <button onClick={handleCopyShareLink} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Referral Link</>}
              </button>
            </div>

            {/* Safety */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-gray-800 dark:text-white text-sm">Safety First</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Mindora automatically filters inappropriate content.
                {totalStats.flaggedCount === 0 ? ' No concerns flagged.' : ` ${totalStats.flaggedCount} item${totalStats.flaggedCount > 1 ? 's' : ''} need review.`}
              </p>
              <div className="flex gap-2">
                <Link to="/parent/flagged" className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium bg-white/80 dark:bg-gray-700 rounded-lg border border-gray-200 hover:bg-white transition-colors">
                  <AlertTriangle className="w-3.5 h-3.5 text-gray-500" /> Flagged
                </Link>
                <Link to="/parent/moderation" className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium bg-white/80 dark:bg-gray-700 rounded-lg border border-gray-200 hover:bg-white transition-colors">
                  <Settings className="w-3.5 h-3.5 text-gray-500" /> Settings
                </Link>
              </div>
            </div>

            {/* Fun Fact */}
            <div className="bg-gradient-to-br from-kid-dandelion/10 to-kid-tangerine/5 rounded-xl border border-kid-dandelion/30 p-4">
              <p className="text-xs font-bold text-gray-600 mb-1 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-kid-dandelion" /> Did you know?</p>
              <p className="text-xs text-gray-600 font-kid">Children who learn for just 15 minutes a day score 20% higher on comprehension tests. Keep the streak going!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
